import { useState } from 'react';
import { T, Var, useGT, useMessages } from 'gt-react';
import { PlusIcon } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  MultiSelect,
  type MultiSelectOption,
} from '@/components/common/MultiSelect';
import {
  AS400_EXPORT_GROUP_SEARCH_COLUMN,
  as400BranchSplitLocationsForDivision,
  as400DivisionCode,
  as400DivisionExportsPerBranch,
  foldToAs400Ascii,
  isAs400Ascii,
} from '@erp/rpc';
import type { As400ExportGroup } from '@erp/rpc';
import {
  AS400_EXPORT_GROUP_LABELS,
  AS400_EXPORT_GROUP_ORDER,
} from './as400Groups';
import type { DivisionOption } from './useDivisionSelection';
import { useDivisionSelection } from './useDivisionSelection';
import {
  chosenNewRowDivisions,
  chosenNewRowGroups,
  initialNewRowDivisionIds,
  newRowLocationLabel,
  newRowTargets,
  pickedNewRowLocations,
} from './newRowTargets';
import type { NewRowDivisionLocations, NewRowTarget } from './newRowTargets';

/**
 * The review Export view's new-row dialog, for a row that goes on several
 * worksheets, divisions or RD locations at once (a single row is typed straight
 * into the blank row at the top of the grid). It authors a net-new worksheet row (a product the AS/400 doesn't have
 * yet). The dialog asks only for the essentials — the row's item number,
 * which worksheets get it, which divisions' upload files carry it, and for RD
 * which of its locations — and hands them to the owner as drafts: each row
 * enters its worksheet as an unsaved edit, is filled in inline like any
 * existing row, and is added to the change set when the user saves.
 *
 * Worksheets are picked several at a time because a net-new product needs a
 * row on most of them (Item, Purchase Data, Price, …): one pass adds it to
 * each worksheet chosen, starting with the one on screen.
 *
 * Divisions are picked several at a time because the same net-new product is
 * usually stocked by more than one: one pass through the dialog authors the
 * item once per division chosen, each its own row under its own CDB, which is
 * what the AS/400 expects (a record is keyed by division as well as by item).
 * Every catalog division is offered, including the ones the grid leaves out
 * because the review has nothing under them: those are precisely where a
 * product that isn't stocked yet gets listed (see {@link chosenNewRowDivisions}).
 *
 * A row's CDB key is the division's code, plus a location on a division whose
 * upload runs per branch (RD, GEN-2720): the dialog asks which of its locations
 * get the item (all by default, at least one) and authors one row per location
 * (`RDA`, `RDC`, …).
 */
export function AddNewRowButton({
  group,
  dataSourceIds,
  onAdd,
  onWillOpen,
}: {
  group: As400ExportGroup;
  /** The divisions currently in view — the dialog's default pick. */
  dataSourceIds: string[];
  /**
   * Stage one authored row per target — a division, or one of RD's locations —
   * on each of `groups`' worksheets, as drafts (nothing is persisted).
   */
  onAdd: (input: {
    groups: As400ExportGroup[];
    targets: NewRowTarget[];
    itemNumber: string;
  }) => void;
  /**
   * Runs just before the dialog opens. The grid uses it to close its open
   * cell editor and release keyboard focus — otherwise Handsontable keeps the
   * cursor in the cell behind the dialog.
   */
  onWillOpen?: () => void;
}) {
  const gt = useGT();
  const m = useMessages();
  const { dataSources } = useDivisionSelection();
  const [open, setOpen] = useState(false);
  const [itemNumber, setItemNumber] = useState('');
  const [checkedGroups, setCheckedGroups] = useState<As400ExportGroup[]>([]);
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  // Per-branch division id → the locations ticked; absent = untouched = all.
  const [pickedLocations, setPickedLocations] = useState<
    Record<string, string[]>
  >({});

  const worksheetsLabel = gt('Worksheets', {
    context: "which of the review's worksheets get the new row",
  });
  const divisionsLabel = gt('Divisions', {
    context: "which divisions' upload files carry the new rows",
  });
  const keyColumn = AS400_EXPORT_GROUP_SEARCH_COLUMN[group];
  const groups = chosenNewRowGroups(checkedGroups);
  const groupOptions: MultiSelectOption[] = AS400_EXPORT_GROUP_ORDER.map(
    (option) => ({ id: option, label: m(AS400_EXPORT_GROUP_LABELS[option]) }),
  );
  const chosenDivisions: DivisionOption[] = chosenNewRowDivisions(
    dataSources,
    checkedIds,
  );
  const divisionOptions: MultiSelectOption[] = dataSources.map(
    (dataSource) => ({ id: dataSource.id, label: dataSource.title }),
  );
  const perBranchDivisions = chosenDivisions.filter((dataSource) =>
    as400DivisionExportsPerBranch(dataSource.title),
  );
  const locationsOf = (dataSourceId: string): NewRowDivisionLocations => {
    const division = perBranchDivisions.find((d) => d.id === dataSourceId);
    if (!division) return { kind: 'division' };
    return {
      kind: 'locations',
      available: as400BranchSplitLocationsForDivision(division.title),
      picked: pickedLocations[dataSourceId] ?? null,
    };
  };
  const { targets, complete } = newRowTargets(
    chosenDivisions.map((dataSource) => ({
      dataSourceId: dataSource.id,
      code: as400DivisionCode(dataSource.title),
    })),
    locationsOf,
  );
  // Already upper-cased and ASCII-folded as it is typed (see the input below);
  // a character with no ASCII spelling survives the fold and blocks the add,
  // the way a space does — the AS/400 can't be sent either.
  const trimmed = itemNumber.trim();
  const canSubmit =
    trimmed !== '' &&
    !/\s/.test(trimmed) &&
    isAs400Ascii(trimmed) &&
    groups.length > 0 &&
    complete;
  const rowCount = targets.length * groups.length;

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setItemNumber('');
      setCheckedGroups([]);
      setCheckedIds([]);
      setPickedLocations({});
    }
  };

  const setDivisionLocations = (dataSourceId: string, codes: string[]) =>
    setPickedLocations((prev) => ({ ...prev, [dataSourceId]: codes }));

  const handleAdd = () => {
    if (!canSubmit) return;
    onAdd({ groups, targets, itemNumber: trimmed });
    handleOpenChange(false);
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          onWillOpen?.();
          setCheckedGroups([group]);
          setCheckedIds(initialNewRowDivisionIds(dataSourceIds));
          setOpen(true);
        }}
      >
        <PlusIcon aria-hidden="true" />
        <T context="button: open the dialog that adds a new row to several worksheets, divisions or RD locations at once">
          Add to Several…
        </T>
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              <T context="dialog title: add a new product row to the review's export worksheet">
                Add a new row
              </T>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              <T context="explains what adding a review export row does">
                Adds a product the AS/400 doesn't have yet as an unsaved row —
                one per worksheet and each division or location you pick. Fill
                in the rest of its columns on each worksheet, then save your
                edits there to keep it.
              </T>
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="new-row-item-number">
                <Var>{keyColumn}</Var>
              </Label>
              <Input
                id="new-row-item-number"
                value={itemNumber}
                onChange={(event) =>
                  setItemNumber(
                    foldToAs400Ascii(event.target.value.toUpperCase()),
                  )
                }
                placeholder={gt('Upper case, no spaces', {
                  context: 'placeholder for a new AS/400 item number',
                })}
                autoFocus
              />
            </div>
            <MultiSelect
              label={worksheetsLabel}
              aria-label={worksheetsLabel}
              placeholder={gt('Pick worksheets', {
                context: 'placeholder of the add-row dialog worksheet picker',
              })}
              options={groupOptions}
              value={groupOptions.filter((option) =>
                groups.some((chosen) => chosen === option.id),
              )}
              onChange={(selected) =>
                setCheckedGroups(
                  AS400_EXPORT_GROUP_ORDER.filter((option) =>
                    selected.some((picked) => picked.id === option),
                  ),
                )
              }
              shouldAutoSort={false}
              hideChips={false}
              fullWidth
            />
            {dataSources.length > 1 && (
              <MultiSelect
                label={divisionsLabel}
                aria-label={divisionsLabel}
                placeholder={gt('Pick divisions', {
                  context: 'placeholder of the add-row dialog division picker',
                })}
                options={divisionOptions}
                value={divisionOptions.filter((option) =>
                  chosenDivisions.some((chosen) => chosen.id === option.id),
                )}
                onChange={(selected) =>
                  setCheckedIds(selected.map((option) => option.id))
                }
                shouldAutoSort={false}
                hideChips={false}
                fullWidth
              />
            )}
            {perBranchDivisions.map((dataSource) => {
              const locations = locationsOf(dataSource.id);
              if (locations.kind !== 'locations') return null;
              return (
                <NewRowLocationPicker
                  key={dataSource.id}
                  divisionTitle={dataSource.title}
                  available={locations.available}
                  picked={pickedNewRowLocations(
                    locations.available,
                    locations.picked,
                  )}
                  onChange={(codes) =>
                    setDivisionLocations(dataSource.id, codes)
                  }
                />
              );
            })}
            {rowCount > 0 && (
              <p className="text-xs text-muted-foreground">
                {rowCount === 1 ? (
                  <T context="confirms the CDB key the new row will be created under">
                    The row will be added under CDB <Var>{targets[0].cdb}</Var>.
                  </T>
                ) : groups.length === 1 ? (
                  <T context="confirms the CDB keys one row each will be created under">
                    <Var>{targets.length}</Var> rows will be added, one under
                    each of CDB{' '}
                    <Var>{targets.map((target) => target.cdb).join(', ')}</Var>.
                  </T>
                ) : (
                  <T context="confirms the new rows across several worksheets: one per CDB key on each worksheet">
                    <Var>{rowCount}</Var> rows will be added: one under each of
                    CDB{' '}
                    <Var>{targets.map((target) => target.cdb).join(', ')}</Var>{' '}
                    on each of the <Var>{groups.length}</Var> worksheets.
                  </T>
                )}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              <T context="cancel adding a new row">Cancel</T>
            </Button>
            <Button onClick={handleAdd} disabled={!canSubmit}>
              {rowCount > 1 ? (
                <T context="confirm adding one new row per chosen division">
                  Add rows
                </T>
              ) : (
                <T context="confirm adding a new row">Add row</T>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** RD's location picker: all picked until the reviewer narrows it. */
function NewRowLocationPicker({
  divisionTitle,
  available,
  picked,
  onChange,
}: {
  divisionTitle: string;
  available: readonly string[];
  picked: string[];
  onChange: (codes: string[]) => void;
}) {
  const gt = useGT();
  const options: MultiSelectOption[] = available.map((code) => ({
    id: code,
    label: newRowLocationLabel(divisionTitle, code),
  }));
  const label = gt('{division} locations', {
    context:
      "which of a division's locations (branches) get the new row, e.g. 'RD locations'",
    division: divisionTitle,
  });
  return (
    <MultiSelect
      label={label}
      aria-label={label}
      placeholder={gt('Pick locations', {
        context: 'placeholder of the add-row dialog location picker',
      })}
      options={options}
      value={options.filter((option) => picked.includes(option.id))}
      onChange={(selected) =>
        onChange(
          available.filter((code) =>
            selected.some((option) => option.id === code),
          ),
        )
      }
      shouldAutoSort={false}
      hideChips={false}
      fullWidth
      error={picked.length === 0}
      helperText={
        picked.length === 0
          ? gt('Pick at least one location.', {
              context:
                'the add-row dialog needs at least one location picked for a per-branch division',
            })
          : undefined
      }
    />
  );
}

/** "N new rows hidden by filters" — shown when filters hide authored rows. */
export function HiddenNewRowsNote({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <p className="text-xs text-muted-foreground">
      <T context="note: authored new rows are hidden while filters are active">
        <Var>{count}</Var> new row(s) hidden while filters are active.
      </T>
    </p>
  );
}
